import { Routes, Route, Link, Navigate, useLocation } from 'react-router-dom'
import { SignedIn, SignedOut, SignIn, SignInButton, UserButton, useAuth } from '@clerk/clerk-react'
import LearnIndex from './pages/LearnIndex'
import LearnCrewPage from './pages/LearnCrewPage'
import ModulePage from './pages/ModulePage'
import ModulePrintPage from './pages/ModulePrintPage'
import EquipmentLab from './pages/EquipmentLab'
import EquipmentLabPanel from './pages/EquipmentLabPanel'
import SandboxEquipment from './pages/SandboxEquipment'
import FacilitatePage from './pages/FacilitatePage'
import PracticeSessionPage from './pages/PracticeSessionPage'
import AdminUsersPage from './pages/AdminUsersPage'
import AdminInvitationsPage from './pages/AdminInvitationsPage'
import AdminProgressPage from './pages/AdminProgressPage'
import ReferencePage from './pages/ReferencePage'
import SourcesPage from './pages/SourcesPage'
import ScoresPage from './pages/ScoresPage'
import SearchResultsPage from './pages/SearchResultsPage'
import SearchBar from './components/SearchBar'
import { CrewSubNav } from './components/learn/CrewSubNav'
import { useUserRole } from './hooks/useUserRole'
import './index.css'

function AppHeader() {
  const { isSignedIn } = useAuth()
  const location = useLocation()
  const pathname = location.pathname
  const signedIn = !!isSignedIn
  const { isFacilitator } = useUserRole()

  const links = signedIn
    ? [
        { href: '/learn', label: 'Learning' },
        { href: '/lab', label: 'Lab' },
        { href: '/reference', label: 'Reference' },
        ...(isFacilitator ? [
          { href: '/facilitate', label: 'Facilitate' },
          { href: '/admin/users', label: 'Admin' },
        ] : []),
      ]
    : []

  const active = (href: string) => pathname === href || pathname.startsWith(href + '/')

  return (
    <header className="app-header">
      <div className="app-header__inner">
        <Link
          to={signedIn ? '/learn' : '/'}
          className="app-header__brand"
        >
          <span className="app-header__mark" aria-hidden="true" />
          <span className="app-header__wordmark">AV Crew Training</span>
        </Link>

        <nav aria-label="Primary" className="app-header__nav">
          <ul className="app-header__links">
            {links.map((l) => (
              <li key={l.href}>
                <Link
                  to={l.href}
                  data-active={active(l.href) ? 'true' : undefined}
                >
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        {signedIn && <SearchBar />}

        <div className="app-header__actions">
          {signedIn ? (
            <UserButton
              appearance={{
                elements: {
                  userButtonAvatarBox: { width: '32px', height: '32px' },
                },
              }}
            />
          ) : (
            <SignInButton mode="modal">
              <button type="button" className="app-header__signin">
                Sign in
              </button>
            </SignInButton>
          )}
        </div>
      </div>
    </header>
  )
}

function LearnSubHeader() {
  const { pathname } = useLocation()
  const show = pathname.startsWith('/learn')
  if (!show) return null
  return (
    <div className="learn-subheader">
      <div className="learn-subheader__inner">
        <CrewSubNav />
      </div>
    </div>
  )
}

function App() {
  return (
    <Routes>
      <Route path="/print/modules/:moduleId" element={<ModulePrintPage />} />
      <Route
        path="*"
        element={
          <div className="app">
            <AppHeader />
            <LearnSubHeader />
            <main>
              <SignedOut>
                <div className="sign-in-container">
                  <SignIn />
                </div>
              </SignedOut>
              <SignedIn>
                <Routes>
                  <Route path="/" element={<Navigate to="/learn" replace />} />
                  <Route path="/learn" element={<LearnIndex />} />
                  <Route path="/learn/:crew" element={<LearnCrewPage />} />
                  <Route path="/audio" element={<Navigate to="/learn/audio" replace />} />
                  <Route path="/video" element={<Navigate to="/learn/video" replace />} />
                  <Route path="/stage" element={<Navigate to="/learn/stage" replace />} />
                  <Route path="/modules/:moduleId" element={<ModulePage />} />
                  <Route path="/lab" element={<EquipmentLab />} />
                  <Route path="/lab/:kind" element={<EquipmentLabPanel />} />
                  <Route path="/scores" element={<ScoresPage />} />
                  <Route path="/reference" element={<ReferencePage />} />
                  <Route path="/reference/sources" element={<SourcesPage />} />
                  <Route path="/search" element={<SearchResultsPage />} />
                  <Route path="/facilitate" element={<FacilitatePage />} />
                  <Route path="/practice/:sessionId" element={<PracticeSessionPage />} />
                  <Route path="/admin" element={<Navigate to="/admin/users" replace />} />
                  <Route path="/admin/users" element={<AdminUsersPage />} />
                  <Route path="/admin/invitations" element={<AdminInvitationsPage />} />
                  <Route path="/admin/progress" element={<AdminProgressPage />} />
                  {import.meta.env.DEV && (
                    <Route path="/sandbox/equipment" element={<SandboxEquipment />} />
                  )}
                  <Route path="*" element={<div>Not Found</div>} />
                </Routes>
              </SignedIn>
            </main>
            <footer className="brand">
              <span>2026 INTERNATIONAL CONVENTION</span>
              <span>MANILA, PHILIPPINES</span>
            </footer>
          </div>
        }
      />
    </Routes>
  )
}

export default App
